import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { companyService } from '../services/companyService';
import { counterpartyService } from '../services/counterpartyService';
import { invoiceService } from '../services/invoiceService';
import { bankTransactionService } from '../services/bankTransactionService';
import { BankAccount } from '../types/company';
import AddBankAccountModal from '../components/AddBankAccountModal';
import EditBankAccountModal from '../components/EditBankAccountModal';
import AddCounterpartyModal from '../components/AddCounterpartyModal';
import EditCounterpartyModal from '../components/EditCounterpartyModal';
import MultiStepInvoiceModal from '../components/MultiStepInvoiceModal';
import EditInvoiceModal from '../components/EditInvoiceModal';
import EnhancedInvoiceImportModal from '../components/EnhancedInvoiceImportModal';
import { BankTransactionsTab } from '../components/BankTransactionsTab';
import PdfViewer from '../components/PdfViewer';
import { DatePickerWithRange } from '../components/ui/date-range-picker';
import { InvoiceTable } from '../components/InvoiceTable';
import { Pagination } from '../components/ui/pagination';
import DownloadLanguageModal from '../components/DownloadLanguageModal';
import { Language } from '../types/language';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import InvoiceNumberingSettings from '../components/InvoiceNumberingSettings';
import CorrectionInvoiceList from '../components/CorrectionInvoiceList';
import CorrectionInvoiceModal from '../components/CorrectionInvoiceModal';
import CompanyUsersTab from '../components/CompanyUsersTab';
import CompanySettingsTab from '../components/CompanySettingsTab';
import ImportCounterpartiesModal from '../components/ImportCounterpartiesModal';
import ImportBankAccountsModal from '../components/ImportBankAccountsModal';
import SyncCounterpartyConfirmModal from '../components/SyncCounterpartyConfirmModal';
import ExpensesTab from '../components/ExpensesTab';
import ContractsTab from '../components/ContractsTab';
import SendInvoiceToWFirmaModal from '../components/SendInvoiceToWFirmaModal';
import { formatCurrency } from '../utils/currency';
import { useAuth } from '../context/AuthContext';
import { companyUserService } from '../services/companyUserService';
import { integrationService } from '../services/integrationService';
import { wfirmaAccountService } from '../services/wfirmaAccountService';
import { fileService } from '../services/fileService';

const CompanyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts' | 'invoices' | 'expenses' | 'contracts' | 'counterparties' | 'bank-transactions' | 'invoice-numbering' | 'correction-invoices' | 'settings'>('overview');
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [isEditInvoiceModalOpen, setIsEditInvoiceModalOpen] = useState(false);
  const [isImportInvoiceModalOpen, setIsImportInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [invoicesRefetch, setInvoicesRefetch] = useState<(() => void) | null>(null);
  const [isEditCounterpartyModalOpen, setIsEditCounterpartyModalOpen] = useState(false);
  const [selectedCounterparty, setSelectedCounterparty] = useState<any>(null);
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isImportCounterpartiesModalOpen, setIsImportCounterpartiesModalOpen] = useState(false);
  const [isImportBankAccountsModalOpen, setIsImportBankAccountsModalOpen] = useState(false);

  const { data: company, isLoading, error, refetch: refetchCompany } = useQuery({
    queryKey: ['company', id],
    queryFn: () => companyService.getCompany(id!),
    enabled: !!id
  });

  // Вкладка пользователей показывается всегда, но доступ определяется внутри компонента CompanyUsersTab

  // Обработка хеша в URL для перехода на нужную вкладку
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['overview', 'accounts', 'invoices', 'expenses', 'contracts', 'counterparties', 'bank-transactions', 'invoice-numbering', 'correction-invoices', 'settings'].includes(hash)) {
      setActiveTab(hash as typeof activeTab);
    }
  }, []);

  // Обновление хеша при изменении вкладки
  useEffect(() => {
    if (activeTab !== 'overview') {
      window.history.replaceState(null, '', `#${activeTab}`);
    } else {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [activeTab]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{t('common.error')}</p>
        <Link to="/companies" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
          {t('companies.backToList')}
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('companies.tabs.overview'), icon: '📊' },
    { id: 'accounts', label: t('companies.tabs.accounts'), icon: '🏦' },
    { id: 'counterparties', label: t('companies.tabs.counterparties'), icon: '👥' },
    { id: 'invoices', label: t('companies.tabs.invoices'), icon: '📄' },
    { id: 'expenses', label: t('companies.tabs.expenses') || 'Расходы', icon: '💸' },
    { id: 'contracts', label: t('companies.tabs.contracts') || 'Контракты', icon: '📚' },
    // { id: 'bank-transactions', label: t('companies.tabs.bankTransactions'), icon: '🏛️' }, // Скрыто, пока не функционально
    { id: 'correction-invoices', label: t('companies.tabs.correctionInvoices'), icon: '📝' },
    { id: 'settings', label: t('companies.tabs.settings') || 'Настройки', icon: '⚙️' }
  ] as const;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Заголовок */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {company.logoPath && (
              <img
                src={fileService.getCompanyLogoUrl(company.logoPath)}
                alt={`${company.name} logo`}
                className="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-gray-50"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{company.name}</h1>
              <p className="text-gray-600 mt-1">{t('companies.taxIdLabel')}: {company.taxId}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link
              to={`/companies/${company.id}/edit`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('companies.edit')}
            </Link>
            <Link
              to="/companies"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('companies.backToList')}
            </Link>
          </div>
        </div>
      </div>

      {/* Табы */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Контент табов */}
      <div className="min-h-96">
        {activeTab === 'overview' && <OverviewTab company={company} />}
        {activeTab === 'accounts' && (
          <AccountsTab 
            company={company} 
            onAddAccount={() => setIsAddAccountModalOpen(true)}
            onEditAccount={(account) => {
              setSelectedBankAccount(account);
              setIsEditAccountModalOpen(true);
            }}
            onImport={() => setIsImportBankAccountsModalOpen(true)}
          />
        )}
        {activeTab === 'invoices' && (
          <InvoicesTab 
            company={company}
            onEditInvoice={(invoice) => {
              setSelectedInvoice(invoice);
              setIsEditInvoiceModalOpen(true);
            }}
            onInvoiceUpdated={(refetchFn) => {
              setInvoicesRefetch(() => refetchFn);
            }}
            isImportModalOpen={isImportInvoiceModalOpen}
            setIsImportModalOpen={setIsImportInvoiceModalOpen}
            systemLanguage={language}
          />
        )}
        {activeTab === 'expenses' && <ExpensesTab companyId={company.id} />}
        {activeTab === 'contracts' && <ContractsTab companyId={company.id} />}
        {activeTab === 'counterparties' && (
          <CounterpartiesTab 
            company={company}
            onEditCounterparty={(counterparty) => {
              setSelectedCounterparty(counterparty);
              setIsEditCounterpartyModalOpen(true);
            }}
            onImport={() => setIsImportCounterpartiesModalOpen(true)}
          />
        )}
        {/* {activeTab === 'bank-transactions' && <BankTransactionsTab company={company} />} */} {/* Скрыто, пока не функционально */}
        {activeTab === 'settings' && <CompanySettingsTab companyId={company.id} />}
        {activeTab === 'correction-invoices' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">{t('correctionInvoices.title')}</h3>
              <button
                onClick={() => setIsCorrectionModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('correctionInvoices.createCorrection')}
              </button>
            </div>
            <CorrectionInvoiceList 
              key={refreshKey}
              companyId={company.id} 
            />
          </div>
        )}
        {/* {activeTab === 'users' && <CompanyUsersTab companyId={company.id} />} */} {/* Перенесено в настройки */}
      </div>

      {/* Модальное окно добавления банковского счета */}
      <AddBankAccountModal
        companyId={company.id}
        isOpen={isAddAccountModalOpen}
        onClose={() => setIsAddAccountModalOpen(false)}
      />

      {/* Модальное окно редактирования банковского счета */}
      <EditBankAccountModal
        companyId={company.id}
        bankAccount={selectedBankAccount}
        isOpen={isEditAccountModalOpen}
        onClose={() => {
          setIsEditAccountModalOpen(false);
          setSelectedBankAccount(null);
        }}
        onSuccess={() => {
          setIsEditAccountModalOpen(false);
          setSelectedBankAccount(null);
          // Обновляем данные компании
          refetchCompany();
        }}
      />

      {/* Модальное окно редактирования инвойса */}
      <EditInvoiceModal
        companyId={company.id}
        invoice={selectedInvoice}
        isOpen={isEditInvoiceModalOpen}
        onClose={() => {
          setIsEditInvoiceModalOpen(false);
          setSelectedInvoice(null);
          // Обновляем данные инвойсов при закрытии модалки
          if (activeTab === 'invoices' && invoicesRefetch) {
            invoicesRefetch();
          }
        }}
        onSuccess={() => {
          setIsEditInvoiceModalOpen(false);
          setSelectedInvoice(null);
          // Обновляем данные инвойсов после успешного редактирования
          if (activeTab === 'invoices' && invoicesRefetch) {
            invoicesRefetch();
          }
        }}
        onInvoiceUpdated={() => {
          // Обновляем данные инвойсов без перезагрузки страницы
          if (activeTab === 'invoices' && invoicesRefetch) {
            invoicesRefetch();
          }
        }}
      />

      {/* Модальное окно редактирования контрагента */}
      <EditCounterpartyModal
        companyId={company.id}
        counterparty={selectedCounterparty}
        isOpen={isEditCounterpartyModalOpen}
        onClose={() => {
          setIsEditCounterpartyModalOpen(false);
          setSelectedCounterparty(null);
        }}
        onSuccess={() => {
          setIsEditCounterpartyModalOpen(false);
          setSelectedCounterparty(null);
        }}
      />

      {/* Модальное окно создания корректировочного счета */}
      <CorrectionInvoiceModal
        isOpen={isCorrectionModalOpen}
        onClose={() => setIsCorrectionModalOpen(false)}
        onSuccess={() => {
          setRefreshKey(prev => prev + 1);
          setIsCorrectionModalOpen(false);
        }}
        companyId={company.id}
      />

      {/* Модальное окно импорта контрагентов */}
      <ImportCounterpartiesModal
        companyId={company.id}
        isOpen={isImportCounterpartiesModalOpen}
        onClose={() => setIsImportCounterpartiesModalOpen(false)}
        onSuccess={() => {
          // Обновляем данные контрагентов
          window.location.reload();
        }}
      />

      {/* Модальное окно импорта банковских счетов */}
      <ImportBankAccountsModal
        companyId={company.id}
        isOpen={isImportBankAccountsModalOpen}
        onClose={() => setIsImportBankAccountsModalOpen(false)}
        onSuccess={() => {
          // Обновляем данные банковских счетов
          window.location.reload();
        }}
      />
    </div>
  );
};

// Компонент вкладки "Обзор"
const OverviewTab: React.FC<{ company: any }> = ({ company }) => {
  const { t } = useTranslation();
  
  // Проверяем наличие логотипа
  const hasLogo = company?.logoPath && company.logoPath.trim() !== '';
  
  // Отладочная информация
  useEffect(() => {
    if (company) {
      console.log('OverviewTab - Company data:', {
        name: company.name,
        logoPath: company.logoPath,
        hasLogo: hasLogo
      });
    }
  }, [company, hasLogo]);
  
  // Загружаем статистику - инвойсы за последние 30 дней
  const { data: invoicesData } = useQuery({
    queryKey: ['invoices-count-last-30-days', company.id],
    queryFn: async () => {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      const params = new URLSearchParams({ 
        page: '0', 
        size: '1',
        startDate: thirtyDaysAgo.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0]
      });
      return invoiceService.getCompanyInvoicesPaginated(company.id, params);
    },
    enabled: !!company.id
  });

  const { data: counterparties = [] } = useQuery({
    queryKey: ['counterparties-count', company.id],
    queryFn: () => counterpartyService.getCompanyCounterparties(company.id),
    enabled: !!company.id
  });

  const { data: transactionsData } = useQuery({
    queryKey: ['transactions-count', company.id],
    queryFn: async () => {
      return bankTransactionService.getCompanyTransactions(company.id, 0, 1);
    },
    enabled: !!company.id
  });

  const invoicesCount = invoicesData?.totalElements || 0;
  const counterpartiesCount = counterparties.length || 0;
  const transactionsCount = transactionsData?.totalElements || 0;
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Основная информация */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-4 mb-4">
          {hasLogo && (
            <img
              src={fileService.getCompanyLogoUrl(company.logoPath)}
              alt={`${company.name} logo`}
              className="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-gray-50 p-1"
              onError={(e) => {
                console.error('Failed to load logo in OverviewTab');
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
              onLoad={() => {
                console.log('Logo loaded successfully in OverviewTab');
              }}
            />
          )}
          <h3 className="text-lg font-semibold text-gray-900">{t('companies.overview.title')}</h3>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">{t('companies.overview.name')}</label>
            <p className="text-gray-900">{company.name}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('companies.overview.taxId')}</label>
            <p className="text-gray-900">{company.taxId}</p>
          </div>
          {company.address && (
            <div>
              <label className="text-sm font-medium text-gray-500">{t('companies.overview.address')}</label>
              <p className="text-gray-900">{company.address}</p>
            </div>
          )}
          {company.phone && (
            <div>
              <label className="text-sm font-medium text-gray-500">{t('companies.overview.phone')}</label>
              <p className="text-gray-900">{company.phone}</p>
            </div>
          )}
          {company.email && (
            <div>
              <label className="text-sm font-medium text-gray-500">{t('companies.overview.email')}</label>
              <p className="text-gray-900">{company.email}</p>
            </div>
          )}
          {company.website && (
            <div>
              <label className="text-sm font-medium text-gray-500">{t('companies.overview.website')}</label>
              <p className="text-gray-900">{company.website}</p>
            </div>
          )}
        </div>
      </div>

      {/* Статистика */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('companies.overview.statistics')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {company.bankAccounts?.length || 0}
            </div>
            <div className="text-sm text-gray-600">{t('companies.overview.bankAccountsCount')}</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{invoicesCount}</div>
            <div className="text-sm text-gray-600">{t('companies.overview.invoicesCount')}</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{counterpartiesCount}</div>
            <div className="text-sm text-gray-600">{t('companies.overview.counterpartiesCount')}</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{transactionsCount}</div>
            <div className="text-sm text-gray-600">{t('companies.overview.transactionsCount')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Компонент вкладки "Счета"
const AccountsTab: React.FC<{ 
  company: any; 
  onAddAccount: () => void;
  onEditAccount: (account: BankAccount) => void;
  onImport: () => void;
}> = ({ company, onAddAccount, onEditAccount, onImport }) => {
  const { t } = useTranslation();
  
  // Проверяем наличие конфигурации wFirma
  const { data: hasWFirmaConfig = false } = useQuery({
    queryKey: ['wfirma-config', company?.id],
    queryFn: () => integrationService.hasWFirmaConfig(company?.id),
    enabled: !!company?.id,
    retry: false
  });

  // Загружаем связи счетов
  const { data: mappingsData } = useQuery({
    queryKey: ['wfirma-account-mappings', company?.id],
    queryFn: () => wfirmaAccountService.getMappings(company?.id),
    enabled: !!company?.id && hasWFirmaConfig && !!company?.bankAccounts && company.bankAccounts.length > 0
  });
  
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{t('companies.accounts.title')}</h3>
          <div className="flex gap-2">
            <button 
              onClick={onImport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              {t('companies.import.import') || 'Импорт'}
            </button>
            <button 
              onClick={onAddAccount}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + {t('companies.accounts.addAccount')}
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {company.bankAccounts && company.bankAccounts.length > 0 ? (
          <div className="space-y-4">
            {company.bankAccounts.map((account: BankAccount) => {
              const isLinked = hasWFirmaConfig && mappingsData && mappingsData[account.id];
              return (
                <div key={account.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-gray-900">{account.name}</h4>
                        {hasWFirmaConfig && (
                          <span 
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              isLinked 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {isLinked 
                              ? (t('wfirma.accounts.synchronized') || 'Синхронизирован') 
                              : (t('wfirma.accounts.notSynchronized') || 'Нет')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{account.bankName}</p>
                      <p className="text-sm text-gray-600 mt-1">{t('companies.accounts.accountNumber')}: {account.accountNumber}</p>
                      {account.iban && <p className="text-sm text-gray-600">IBAN: {account.iban}</p>}
                      {account.swift && <p className="text-sm text-gray-600">SWIFT: {account.swift}</p>}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">
                          {formatCurrency(account.balance || 0, account.currency)}
                        </div>
                        <div className="text-sm text-gray-500">{t('companies.accounts.balance')}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => onEditAccount(account)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          {t('companies.accounts.edit')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">{t('companies.accounts.noAccounts')}</p>
            <button 
              onClick={onAddAccount}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('companies.accounts.addFirstAccount')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Компонент вкладки "Инвойсы"
const InvoicesTab: React.FC<{ 
  company: any;
  onEditInvoice: (invoice: any) => void;
  onInvoiceUpdated: (refetchFn: () => void) => void;
  isImportModalOpen: boolean;
  setIsImportModalOpen: (open: boolean) => void;
  systemLanguage: Language;
}> = ({ company, onEditInvoice, onInvoiceUpdated, isImportModalOpen, setIsImportModalOpen, systemLanguage }) => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [copyFromInvoice, setCopyFromInvoice] = useState<any>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [downloadLanguageModalOpen, setDownloadLanguageModalOpen] = useState(false);
  const [invoiceToDownload, setInvoiceToDownload] = useState<any>(null);
  
  // Состояние для модального окна отправки в wFirma
  const [sendToWFirmaModal, setSendToWFirmaModal] = useState<{
    isOpen: boolean;
    invoice: any;
    status: 'confirm' | 'syncing' | 'success' | 'error';
    errorMessage?: string;
  }>({
    isOpen: false,
    invoice: null,
    status: 'confirm'
  });
  const [bulkDraftSend, setBulkDraftSend] = useState<{
    isRunning: boolean;
    done: boolean;
    total: number;
    processed: number;
    successCount: number;
    currentInvoiceNumber?: string;
    errorMessage?: string;
  }>({
    isRunning: false,
    done: false,
    total: 0,
    processed: 0,
    successCount: 0
  });
  
  const [hasWFirmaConfig, setHasWFirmaConfig] = useState<boolean>(false);
  
  // Infinite scroll
  const pageSize = 25;
  
  // Проверяем наличие wFirma конфигурации
  useEffect(() => {
    if (company?.id) {
      integrationService.hasWFirmaConfig(company.id)
        .then(setHasWFirmaConfig)
        .catch(() => setHasWFirmaConfig(false));
    }
  }, [company?.id]);
  
  // Фильтры по дате
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined
  });
  
  // Загрузка инвойсов с infinite scroll
  const { 
    data, 
    isLoading, 
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage 
  } = useInfiniteQuery({
    queryKey: ['invoices-paginated', company.id, dateRange, searchTerm, statusFilter],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        page: pageParam.toString(),
        size: pageSize.toString(),
      });

      if (dateRange.from) {
        params.append('startDate', dateRange.from.toISOString().split('T')[0]);
      }
      if (dateRange.to) {
        params.append('endDate', dateRange.to.toISOString().split('T')[0]);
      }

      // Передаем параметры поиска и статуса на бэкенд
      if (searchTerm && searchTerm.trim() !== '') {
        params.append('searchTerm', searchTerm.trim());
      }
      if (statusFilter && statusFilter !== 'ALL') {
        params.append('status', statusFilter);
      }

      return invoiceService.getCompanyInvoicesPaginated(company.id, params);
    },
    getNextPageParam: (lastPage, allPages) => {
      // Если последняя страница содержит меньше элементов, чем pageSize, значит это последняя страница
      // Также проверяем, что общее количество загруженных элементов меньше totalElements
      const totalLoaded = allPages.reduce((sum, page) => sum + page.content.length, 0);
      if (lastPage.content.length < pageSize || totalLoaded >= (lastPage.totalElements || 0)) {
        return undefined; // Нет следующей страницы
      }
      return allPages.length; // Следующая страница
    },
    initialPageParam: 0,
  });
  
  // Объединяем все страницы в один массив
  const invoices = data?.pages.flatMap(page => page.content) || [];
  const totalElements = data?.pages[0]?.totalElements || 0;

  // Передаем функцию refetch в родительский компонент
  useEffect(() => {
    onInvoiceUpdated(refetch);
  }, [refetch, onInvoiceUpdated]);

  // Обработчики для фильтров
  const handleDateRangeChange = (range: { from: Date | undefined; to: Date | undefined }) => {
    setDateRange(range);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
  };

  const clearFilters = () => {
    setDateRange({ from: undefined, to: undefined });
    setSearchTerm('');
    setStatusFilter('ALL');
  };

  // Обработчик скролла для infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      // Проверяем, достигли ли мы конца страницы (с небольшим отступом в 200px)
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      if (scrollTop + windowHeight >= documentHeight - 200) {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDownloadClick = (invoice: any) => {
    setInvoiceToDownload(invoice);
    setDownloadLanguageModalOpen(true);
  };

  const handleDownloadConfirm = (language: Language) => {
    if (invoiceToDownload) {
      // Используем сохраненный язык из фактуры, если он есть, иначе используем выбранный язык
      const pdfLanguage = (invoiceToDownload.pdfLanguage as Language) || language;
      invoiceService.downloadInvoicePdf(company.id, invoiceToDownload.id, invoiceToDownload.invoiceNumber, invoiceToDownload.clientName, pdfLanguage);
    }
  };

  // Обработчики для модального окна отправки в wFirma
  const handleSendToWFirmaConfirm = async (forceResync?: boolean) => {
    if (!sendToWFirmaModal.invoice) return;
    
    setSendToWFirmaModal(prev => ({ ...prev, status: 'syncing' }));
    
    try {
      await invoiceService.updateInvoiceStatus(sendToWFirmaModal.invoice.id, 'SENT', forceResync);
      setSendToWFirmaModal(prev => ({ ...prev, status: 'success' }));
      refetch();
      
      // Автоматически закрываем через 2 секунды
      setTimeout(() => {
        setSendToWFirmaModal({ isOpen: false, invoice: null, status: 'confirm' });
      }, 2000);
    } catch (error: any) {
      console.error('Error sending invoice to wFirma:', error);
      setSendToWFirmaModal(prev => ({
        ...prev,
        status: 'error',
        errorMessage: error?.response?.data?.message || error?.message || 'Ошибка отправки фактуры в wFirma'
      }));
    }
  };
  
  const handleSyncCounterpartyForInvoice = async () => {
    if (!sendToWFirmaModal.invoice?.client?.id) return;
    
    try {
      await counterpartyService.syncCounterpartyToWFirma(company.id, sendToWFirmaModal.invoice.client.id);
      // Обновляем фактуру, чтобы получить обновленные данные контрагента
      refetch();
      // Закрываем модальное окно синхронизации контрагента и открываем модальное окно отправки фактуры
      setSendToWFirmaModal(prev => ({
        ...prev,
        status: 'confirm'
      }));
    } catch (error: any) {
      console.error('Error syncing counterparty:', error);
      setSendToWFirmaModal(prev => ({
        ...prev,
        status: 'error',
        errorMessage: error?.response?.data?.message || error?.message || 'Ошибка синхронизации контрагента'
      }));
    }
  };
  
  const handleSendToWFirmaClose = () => {
    setSendToWFirmaModal({ isOpen: false, invoice: null, status: 'confirm' });
  };

  const loadAllDraftInvoices = async () => {
    const size = 100;
    let page = 0;
    const allDrafts: any[] = [];

    while (true) {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        status: 'DRAFT'
      });
      const response = await invoiceService.getCompanyInvoicesPaginated(company.id, params);
      const content = response?.content || [];
      allDrafts.push(...content);

      if (response?.last || content.length === 0) {
        break;
      }
      page += 1;
    }

    const invoiceNumberCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

    return allDrafts.sort((a, b) => {
      const aTime = new Date(a.issueDate).getTime();
      const bTime = new Date(b.issueDate).getTime();
      if (aTime !== bTime) {
        return aTime - bTime;
      }

      const aNumber = a.invoiceNumber || '';
      const bNumber = b.invoiceNumber || '';
      return invoiceNumberCollator.compare(aNumber, bNumber);
    });
  };

  const handleSendAllDraftInvoices = async () => {
    const confirmed = window.confirm(
      t('companies.invoices.sendAllDraftsConfirm') ||
        'Отправить все фактуры со статусом "Черновик" по очереди (по дате выставления)?'
    );
    if (!confirmed) return;

    try {
      const drafts = await loadAllDraftInvoices();
      if (drafts.length === 0) {
        window.alert(t('companies.invoices.sendAllDraftsNoDrafts') || 'Нет фактур со статусом "Черновик"');
        return;
      }

      setBulkDraftSend({
        isRunning: true,
        done: false,
        total: drafts.length,
        processed: 0,
        successCount: 0,
        currentInvoiceNumber: drafts[0]?.invoiceNumber
      });

      for (let i = 0; i < drafts.length; i++) {
        const inv = drafts[i];
        setBulkDraftSend(prev => ({
          ...prev,
          currentInvoiceNumber: inv.invoiceNumber
        }));

        try {
          await invoiceService.updateInvoiceStatus(inv.id, 'SENT');
          setBulkDraftSend(prev => ({
            ...prev,
            processed: i + 1,
            successCount: prev.successCount + 1
          }));
        } catch (error: any) {
          const serverMessage =
            error?.response?.data?.message || error?.message || 'Ошибка отправки';
          setBulkDraftSend(prev => ({
            ...prev,
            isRunning: false,
            done: false,
            errorMessage: `${inv.invoiceNumber}: ${serverMessage}`
          }));
          return;
        }
      }

      setBulkDraftSend(prev => ({
        ...prev,
        isRunning: false,
        done: true,
        errorMessage: undefined
      }));
      refetch();
    } catch (error: any) {
      setBulkDraftSend({
        isRunning: false,
        done: false,
        total: 0,
        processed: 0,
        successCount: 0,
        errorMessage: error?.response?.data?.message || error?.message || 'Ошибка запуска процесса'
      });
    }
  };

  // Поиск и фильтрация теперь происходят на бэкенде, поэтому используем invoices напрямую
  const filteredInvoices = invoices;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('companies.invoices.title')}</h3>
            <p className="text-sm text-gray-600">{t('companies.invoices.totalInvoices') || 'Всего инвойсов'}: {totalElements}</p>
          </div>
          <div className="flex gap-3 flex-wrap justify-end">
            <button
              onClick={handleSendAllDraftInvoices}
              disabled={bulkDraftSend.isRunning}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkDraftSend.isRunning
                ? t('companies.invoices.sendAllDraftsInProgress') || 'Идет отправка...'
                : t('companies.invoices.sendAllDrafts') || 'Отправить все черновики'}
            </button>
            <button 
              onClick={() => setIsImportModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              {t('companies.invoices.importInvoice')}
            </button>
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + {t('companies.invoices.createInvoice')}
            </button>
          </div>
        </div>
      </div>

      {(bulkDraftSend.isRunning || bulkDraftSend.done || bulkDraftSend.errorMessage) && (
        <div className="px-6 pt-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-700 mb-2">
              {bulkDraftSend.isRunning
                ? (t('companies.invoices.sendAllDraftsProgress') || 'Обработка фактур') +
                  `: ${bulkDraftSend.processed}/${bulkDraftSend.total}` +
                  (bulkDraftSend.currentInvoiceNumber ? ` (${bulkDraftSend.currentInvoiceNumber})` : '')
                : bulkDraftSend.done
                  ? (t('companies.invoices.sendAllDraftsDone') || 'Готово') +
                    `: ${bulkDraftSend.successCount}/${bulkDraftSend.total}`
                  : ''}
            </p>
            {bulkDraftSend.total > 0 && (
              <div className="w-full h-2 bg-gray-200 rounded">
                <div
                  className="h-2 bg-purple-600 rounded transition-all duration-300"
                  style={{
                    width: `${Math.round((bulkDraftSend.processed / bulkDraftSend.total) * 100)}%`
                  }}
                />
              </div>
            )}
            {bulkDraftSend.errorMessage && (
              <p className="text-sm text-red-700 mt-2">
                {t('companies.invoices.sendAllDraftsFailed') || 'Процесс остановлен из-за ошибки'}: {bulkDraftSend.errorMessage}
              </p>
            )}
          </div>
        </div>
      )}
      
      <div className="p-6">
        {/* Фильтры и поиск */}
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <h4 className="text-lg font-semibold mb-4">{t('common.filters')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.search')}</label>
              <input
                type="text"
                placeholder={t('companies.invoices.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('invoices.status')}</label>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">{t('companies.invoices.allStatuses')}</option>
                <option value="DRAFT">{t('companies.invoices.statuses.DRAFT')}</option>
                <option value="SENT">{t('companies.invoices.statuses.SENT')}</option>
                <option value="PAID">{t('companies.invoices.statuses.PAID')}</option>
                <option value="OVERDUE">{t('companies.invoices.statuses.OVERDUE')}</option>
                <option value="CANCELLED">{t('companies.invoices.statuses.CANCELLED')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.period')}</label>
              <DatePickerWithRange
                date={dateRange}
                onDateChange={handleDateRangeChange}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button 
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.clearFilters')}
            </button>
          </div>
        </div>

        {/* Список инвойсов */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
            <p className="mt-2 text-gray-500">{t('companies.invoices.loading')}</p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">
              {searchTerm || statusFilter !== 'ALL' ? t('companies.invoices.noInvoicesFound') : t('companies.invoices.noInvoices')}
            </p>
            {searchTerm || statusFilter !== 'ALL' ? (
              <button 
                onClick={clearFilters}
                className="px-4 py-2 text-blue-600 hover:underline"
              >
                {t('companies.invoices.clearFilters')}
              </button>
            ) : (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('companies.invoices.createFirstInvoice')}
              </button>
            )}
          </div>
        ) : (
          <>
            <InvoiceTable 
              invoices={filteredInvoices}
              companyId={company.id}
              onView={(invoice) => {
                setSelectedInvoice(invoice);
                setPdfViewerOpen(true);
              }}
              onEdit={onEditInvoice}
              onDownload={handleDownloadClick}
              onCopy={(invoice) => {
                setCopyFromInvoice(invoice);
                setIsAddModalOpen(true);
              }}
              onStatusChange={async (invoiceId, newStatus) => {
                // Если статус изменяется на SENT
                if (newStatus === 'SENT') {
                  // Находим фактуру в списке
                  const invoice = filteredInvoices.find((inv: any) => inv.id === invoiceId);
                  if (invoice) {
                    // Если есть wFirma конфигурация, показываем модальное окно с синхронизацией
                    if (hasWFirmaConfig) {
                      // Проверяем наличие wfirmaId у контрагента (используем clientWfirmaId из DTO)
                      const needsCounterpartySync = !invoice.clientWfirmaId && !invoice.client?.wfirmaId;
                      setSendToWFirmaModal({
                        isOpen: true,
                        invoice: {
                          ...invoice,
                          client: {
                            id: invoice.clientId,
                            name: invoice.clientName,
                            wfirmaId: invoice.clientWfirmaId
                          }
                        },
                        status: 'confirm',
                        errorMessage: undefined
                      });
                      return; // Не меняем статус сразу, ждем подтверждения
                    } else {
                      // Если нет интеграции, показываем простое подтверждение
                      const confirmed = window.confirm(
                        t('invoices.confirmStatusChange') || 
                        `Вы уверены, что хотите изменить статус инвойса "${invoice.invoiceNumber}" на "Отправлен"?`
                      );
                      if (!confirmed) {
                        return; // Пользователь отменил
                      }
                    }
                  }
                }
                
                // Обычное изменение статуса
                try {
                  await invoiceService.updateInvoiceStatus(invoiceId, newStatus);
                  refetch(); // Обновляем список инвойсов
                } catch (error: any) {
                  console.error('Error updating invoice status:', error);
                  // Ошибка будет обработана через apiClient interceptor, который покажет toast-уведомление
                  // Не показываем alert, чтобы избежать дублирования
                }
              }}
              showClient={true}
            />
            
            {/* Индикатор загрузки при скролле */}
            {isFetchingNextPage && (
              <div className="mt-4 text-center">
                <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></div>
                <p className="mt-2 text-sm text-gray-500">{t('common.loading') || 'Загрузка...'}</p>
              </div>
            )}
            
            {/* Индикатор конца списка */}
            {!hasNextPage && filteredInvoices.length > 0 && (
              <div className="mt-4 text-center text-sm text-gray-500">
                {t('companies.invoices.allInvoicesLoaded') || 'Все инвойсы загружены'}
              </div>
            )}
          </>
        )}
      </div>

      {/* Модальное окно добавления инвойса */}
      <MultiStepInvoiceModal
        companyId={company.id}
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setCopyFromInvoice(null);
          // Обновляем данные при закрытии модалки создания
          refetch();
        }}
        onSuccess={() => {
          setIsAddModalOpen(false);
          setCopyFromInvoice(null);
          // Обновляем данные после успешного создания
          refetch();
        }}
        copyFromInvoice={copyFromInvoice}
      />

      {/* Модальное окно импорта инвойса */}
      <EnhancedInvoiceImportModal
        companyId={company.id}
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          // Обновляем данные при закрытии модалки импорта
          refetch();
        }}
        onSuccess={() => {
          setIsImportModalOpen(false);
          // Обновляем данные после успешного импорта
          refetch();
        }}
      />

      {/* Модальное окно просмотра PDF */}
      {selectedInvoice && (
        <PdfViewer
          companyId={company.id}
          invoiceId={selectedInvoice.id}
          invoiceNumber={selectedInvoice.invoiceNumber}
          clientName={selectedInvoice.clientName}
          pdfLanguage={selectedInvoice.pdfLanguage}
          isOpen={pdfViewerOpen}
          onClose={() => {
            setPdfViewerOpen(false);
            setSelectedInvoice(null);
          }}
        />
      )}

      {/* Модальное окно выбора языка для скачивания */}
      <DownloadLanguageModal
        isOpen={downloadLanguageModalOpen}
        onClose={() => {
          setDownloadLanguageModalOpen(false);
          setInvoiceToDownload(null);
        }}
        onConfirm={handleDownloadConfirm}
        currentLanguage={(invoiceToDownload?.pdfLanguage as Language) || systemLanguage}
        invoiceNumber={invoiceToDownload?.invoiceNumber || ''}
      />

      {/* Модальное окно отправки фактуры в wFirma */}
      {sendToWFirmaModal.invoice && (
        <SendInvoiceToWFirmaModal
          isOpen={sendToWFirmaModal.isOpen}
          onClose={handleSendToWFirmaClose}
          onConfirm={handleSendToWFirmaConfirm}
          onSyncCounterparty={handleSyncCounterpartyForInvoice}
          invoiceNumber={sendToWFirmaModal.invoice.invoiceNumber}
          invoiceWfirmaId={sendToWFirmaModal.invoice.wfirmaId}
          counterpartyName={sendToWFirmaModal.invoice.client?.name || ''}
          counterpartyWfirmaId={sendToWFirmaModal.invoice.client?.wfirmaId}
          companyId={company.id}
          needsCounterpartySync={!sendToWFirmaModal.invoice.client?.wfirmaId}
          status={sendToWFirmaModal.status}
          errorMessage={sendToWFirmaModal.errorMessage}
        />
      )}
    </div>
  );
};

// Компонент вкладки "Контрагенты"
const CounterpartiesTab: React.FC<{ 
  company: any;
  onEditCounterparty: (counterparty: any) => void;
  onImport: () => void;
}> = ({ company, onEditCounterparty, onImport }) => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncConfirmModal, setSyncConfirmModal] = useState<{ isOpen: boolean; counterparty: any }>({
    isOpen: false,
    counterparty: null
  });
  
  const { data: counterparties = [], isLoading, refetch } = useQuery({
    queryKey: ['counterparties', company.id],
    queryFn: () => counterpartyService.getCompanyCounterparties(company.id)
  });

  const deleteCounterpartyMutation = useMutation({
    mutationFn: (counterpartyId: string) => 
      counterpartyService.deleteCounterparty(company.id, counterpartyId),
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      console.error('Ошибка удаления контрагента:', error);
      window.alert('Ошибка удаления контрагента');
    }
  });

  const [syncStatus, setSyncStatus] = useState<'confirm' | 'syncing' | 'success' | 'error'>('confirm');
  const [syncErrorMessage, setSyncErrorMessage] = useState<string>('');
  const [bulkSyncStatus, setBulkSyncStatus] = useState<'idle' | 'syncing' | 'unsyncing' | 'success' | 'error'>('idle');
  const [bulkSyncMessage, setBulkSyncMessage] = useState<string>('');

  const unsyncAllMutation = useMutation({
    mutationFn: () => counterpartyService.unsyncAllCounterpartiesFromWFirma(company.id),
    onSuccess: (data) => {
      refetch();
      setBulkSyncStatus('success');
      setBulkSyncMessage(data?.message || t('companies.counterparties.unsyncAllSuccess') || 'Привязка сброшена');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 3000);
    },
    onError: (error: any) => {
      setBulkSyncStatus('error');
      setBulkSyncMessage(error?.response?.data?.message || error?.message || 'Ошибка');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 5000);
    }
  });

  const syncAllMutation = useMutation({
    mutationFn: () => counterpartyService.syncAllCounterpartiesToWFirma(company.id),
    onSuccess: () => {
      refetch();
      setBulkSyncStatus('success');
      setBulkSyncMessage(t('companies.counterparties.syncAllSuccess') || 'Все контрагенты синхронизированы');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 3000);
    },
    onError: (error: any) => {
      setBulkSyncStatus('error');
      setBulkSyncMessage(error?.response?.data?.message || error?.message || 'Ошибка');
      setTimeout(() => { setBulkSyncStatus('idle'); setBulkSyncMessage(''); }, 5000);
    }
  });

  const syncCounterpartyMutation = useMutation({
    mutationFn: (counterpartyId: string) => 
      counterpartyService.syncCounterpartyToWFirma(company.id, counterpartyId),
    onSuccess: () => {
      refetch();
      setSyncStatus('success');
      // Автоматически закрываем модальное окно через 2 секунды после успеха
      setTimeout(() => {
        setSyncConfirmModal({ isOpen: false, counterparty: null });
        setSyncStatus('confirm');
        setSyncErrorMessage('');
      }, 2000);
    },
    onError: (error: any) => {
      console.error('Ошибка синхронизации контрагента:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Ошибка синхронизации контрагента';
      setSyncErrorMessage(errorMessage);
      setSyncStatus('error');
    }
  });

  const handleSyncClick = (counterparty: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Предотвращаем переход на страницу контрагента
    
    if (counterparty.syncStatus === 'NOT_SYNCED') {
      setSyncStatus('confirm');
      setSyncErrorMessage('');
      setSyncConfirmModal({ isOpen: true, counterparty });
    }
  };

  const handleSyncConfirm = () => {
    if (syncConfirmModal.counterparty) {
      setSyncStatus('syncing');
      syncCounterpartyMutation.mutate(syncConfirmModal.counterparty.id);
    }
  };

  const handleSyncModalClose = () => {
    setSyncConfirmModal({ isOpen: false, counterparty: null });
    setSyncStatus('confirm');
    setSyncErrorMessage('');
  };

  const handleDeleteCounterparty = (counterpartyId: string, counterpartyName: string) => {
    if (window.confirm(`${t('companies.counterparties.deleteConfirm')} "${counterpartyName}"?`)) {
      deleteCounterpartyMutation.mutate(counterpartyId);
    }
  };

  const filteredCounterparties = counterparties.filter(counterparty =>
    counterparty.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    counterparty.taxId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    counterparty.vatId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    counterparty.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    counterparty.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    counterparty.comment?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{t('companies.counterparties.title')}</h3>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => window.confirm(t('companies.counterparties.unsyncAllConfirm') || 'Сбросить привязку к wFirma у всех контрагентов? После этого можно будет заново синхронизировать с новой учётной записью wFirma.') && unsyncAllMutation.mutate()}
              disabled={unsyncAllMutation.isPending || syncAllMutation.isPending}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
              title={t('companies.counterparties.unsyncAllHint') || 'Отвязать всех от старой wFirma'}
            >
              {unsyncAllMutation.isPending ? (t('common.loading') || '...') : (t('companies.counterparties.unsyncAll') || 'Отвязать от wFirma')}
            </button>
            <button
              onClick={() => syncAllMutation.mutate()}
              disabled={unsyncAllMutation.isPending || syncAllMutation.isPending}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              title={t('companies.counterparties.syncAllHint') || 'Добавить всех контрагентов в wFirma'}
            >
              {syncAllMutation.isPending ? (t('common.loading') || '...') : (t('companies.counterparties.syncAll') || 'Синхр. в wFirma')}
            </button>
            <button 
              onClick={onImport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              {t('companies.import.import') || 'Импорт'}
            </button>
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + {t('companies.counterparties.addCounterparty')}
            </button>
          </div>
        </div>
        {(bulkSyncStatus === 'success' || bulkSyncStatus === 'error') && (
          <p className={`mt-2 text-sm ${bulkSyncStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {bulkSyncMessage}
          </p>
        )}
      </div>
      
      <div className="p-6">
        {/* Поиск */}
        <div className="mb-6">
          <input
            type="text"
            placeholder={t('companies.counterparties.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Список контрагентов */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
            <p className="mt-2 text-gray-500">{t('companies.counterparties.loading')}</p>
          </div>
        ) : filteredCounterparties.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">
              {searchTerm ? t('companies.counterparties.noCounterpartiesFound') : t('companies.counterparties.noCounterparties')}
            </p>
            {searchTerm ? (
              <button 
                onClick={() => setSearchTerm('')}
                className="px-4 py-2 text-blue-600 hover:underline"
              >
                {t('companies.counterparties.clearSearch')}
              </button>
            ) : (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('companies.counterparties.addFirstCounterparty')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCounterparties.map((counterparty) => (
              <div key={counterparty.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => window.location.href = `/companies/${company.id}/counterparties/${counterparty.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-lg text-blue-600 hover:text-blue-800">{counterparty.name}</h4>
                      {counterparty.syncStatus && (
                        <span 
                          onClick={(e) => handleSyncClick(counterparty, e)}
                          className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors ${
                            counterparty.syncStatus === 'SYNCED' 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : counterparty.syncStatus === 'NOT_SYNCED'
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                          title={counterparty.syncStatus === 'NOT_SYNCED' 
                            ? (t('companies.counterparties.syncClickHint') || 'Нажмите для синхронизации с wFirma')
                            : ''}
                        >
                          {counterparty.syncStatus === 'SYNCED' 
                            ? t('companies.counterparties.syncStatus.synced')
                            : counterparty.syncStatus === 'NOT_SYNCED'
                            ? t('companies.counterparties.syncStatus.notSynced')
                            : t('companies.counterparties.syncStatus.noSync')
                          }
                        </span>
                      )}
                    </div>
                    {counterparty.taxId && (
                      <p className="text-sm text-gray-600">{t('companies.counterparties.taxId')}: {counterparty.taxId}</p>
                    )}
                    {counterparty.vatId && (
                      <p className="text-sm text-gray-600">{t('companies.counterparties.vatId')}: {counterparty.vatId}</p>
                    )}
                    {counterparty.contactPerson && (
                      <p className="text-sm text-gray-600">{t('companies.counterparties.contact')}: {counterparty.contactPerson}</p>
                    )}
                    {counterparty.email && (
                      <p className="text-sm text-gray-600">{t('companies.counterparties.email')}: {counterparty.email}</p>
                    )}
                    {counterparty.phone && (
                      <p className="text-sm text-gray-600">{t('companies.counterparties.phone')}: {counterparty.phone}</p>
                    )}
                    {counterparty.city && (
                      <p className="text-sm text-gray-600">{t('companies.counterparties.city') || 'Город'}: {counterparty.city}</p>
                    )}
                    {counterparty.address && (
                      <p className="text-sm text-gray-600">{t('companies.counterparties.address')}: {counterparty.address}</p>
                    )}
                    {counterparty.comment && (
                      <p className="text-sm text-gray-600 mt-2 italic">{t('companies.counterparties.comment') || 'Комментарий'}: {counterparty.comment}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => onEditCounterparty(counterparty)}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      {t('companies.counterparties.edit')}
                    </button>
                    <button 
                      onClick={() => handleDeleteCounterparty(counterparty.id, counterparty.name)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      {t('companies.counterparties.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно добавления контрагента */}
      <AddCounterpartyModal
        companyId={company.id}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setIsAddModalOpen(false);
          refetch();
        }}
      />

      {/* Модальное окно подтверждения синхронизации */}
      <SyncCounterpartyConfirmModal
        isOpen={syncConfirmModal.isOpen}
        onClose={handleSyncModalClose}
        onConfirm={handleSyncConfirm}
        counterpartyName={syncConfirmModal.counterparty?.name || ''}
        status={syncStatus}
        errorMessage={syncErrorMessage}
      />
    </div>
  );
};

export default CompanyDetails;
